import { useState, useEffect, useCallback } from "react";
import { formatCycleDate } from "../../utils/dateUtils";

const NAVY  = "#1A3B6E";
const MID   = "#2C5FA8";
const BRIGHT = "#3B7DD8";
const LIGHT = "#E8F2FB";

const STATUS_CONFIG = {
  submitted:       { label: "Submitted",      color: BRIGHT },
  packed:          { label: "Packed",          color: "#16A34A" },
  picked_up:       { label: "Picked up",       color: "#15803D" },
  no_show:         { label: "No-show",         color: "#DC2626" },
  not_picked_up:   { label: "Not picked up",   color: "#EF4444" },
  flagged:         { label: "Flagged",         color: "#D97706" },
  cancelled:       { label: "Cancelled",       color: "#6B7280" },
};

const FLAG_LABELS = {
  missing_from_shelf: "Missing from shelf",
  damaged:            "Damaged",
  wrong_quantity:     "Wrong quantity",
  other:              "Other",
};

const CYCLE_STATUS_COLORS = {
  open:   { bg: "#DCFCE7", text: "#15803D" },
  active: { bg: "#DBEAFE", text: "#1D4ED8" },
  closed: { bg: "#F3F4F6", text: "#374151" },
  draft:  { bg: "#FEF9C3", text: "#854D0E" },
};

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatCard({ label, value, sub, accent }) {
  return (
    <div style={{
      background: "#FFFFFF",
      border: `1.5px solid ${LIGHT}`,
      borderTop: `3px solid ${accent || NAVY}`,
      borderRadius: 12,
      padding: "18px 20px",
      flex: "1 1 150px",
      minWidth: 130,
    }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, color: NAVY, lineHeight: 1, marginBottom: 4 }}>
        {value}
      </div>
      {sub && <div style={{ fontSize: 12, color: "#718096" }}>{sub}</div>}
    </div>
  );
}

function SectionCard({ title, children, style }) {
  return (
    <div style={{ background: "#FFFFFF", border: `1.5px solid ${LIGHT}`, borderRadius: 12, overflow: "hidden", marginBottom: 16, ...style }}>
      <div style={{ padding: "12px 20px", borderBottom: `1px solid ${LIGHT}` }}>
        <h3 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: 0 }}>{title}</h3>
      </div>
      <div style={{ padding: "16px 20px" }}>
        {children}
      </div>
    </div>
  );
}

function RateBar({ label, count, total, rate, color }) {
  const pct = total != null && total > 0 ? Math.round((count / total) * 100) : (rate ?? 0);
  const display = count != null ? `${(count).toLocaleString()} (${pct}%)` : `${pct}%`;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
        <span style={{ fontSize: 13, color: "#4A5568" }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color: NAVY }}>{display}</span>
      </div>
      <div style={{ height: 7, background: LIGHT, borderRadius: 4, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 4, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

function VelocityChart({ data }) {
  if (!data?.length) {
    return (
      <div style={{ fontSize: 13, color: "#9CA3AF", textAlign: "center", padding: "20px 0" }}>
        No submission data for this period
      </div>
    );
  }
  const maxCount = Math.max(...data.map(d => d.count), 1);
  const BAR_W = 26, GAP = 6, CH = 72;
  const totalW = data.length * (BAR_W + GAP) - GAP;

  return (
    <div style={{ overflowX: "auto", overflowY: "hidden" }}>
      <svg width={Math.max(totalW, 280)} height={CH + 26} style={{ display: "block" }}>
        {data.map((d, i) => {
          const barH = Math.max(Math.round((d.count / maxCount) * CH), 3);
          const x = i * (BAR_W + GAP);
          const y = CH - barH;
          return (
            <g key={d.date}>
              <rect x={x} y={y} width={BAR_W} height={barH} fill={MID} rx={2} opacity="0.85" />
              <text x={x + BAR_W / 2} y={CH + 14} fontSize={9} textAnchor="middle" fill="#718096">
                {d.date.slice(5)}
              </text>
              {d.count > 0 && (
                <text x={x + BAR_W / 2} y={Math.max(y - 3, 10)} fontSize={9} textAnchor="middle" fill={NAVY} fontWeight="600">
                  {d.count}
                </text>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

function CycleStatusBadge({ status }) {
  const cfg = CYCLE_STATUS_COLORS[status] || { bg: "#F3F4F6", text: "#374151" };
  return (
    <span style={{ background: cfg.bg, color: cfg.text, fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 20 }}>
      {status}
    </span>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function MetricsTab({ user, auth }) {
  const [cycles, setCycles]               = useState([]);
  const [selectedCycle, setSelectedCycle] = useState(null); // null = awaiting init
  const [refreshKey, setRefreshKey]       = useState(0);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState(null);
  const [data, setData]                   = useState(null);

  const getToken = useCallback(async () => {
    const u = auth?.currentUser;
    if (!u) throw new Error("Not authenticated");
    return u.getIdToken();
  }, [auth]);

  // ── Load cycles on mount, default to current open/active ─────────────────────
  useEffect(() => {
    let active = true;
    getToken()
      .then(token =>
        fetch("/api/cycles", { headers: { Authorization: `Bearer ${token}` } })
      )
      .then(r => r.json())
      .then(body => {
        if (!active) return;
        const list = (body.cycles || []).filter(c => c.status !== "draft");
        setCycles(list);
        const cur = list.find(c => c.status === "open" || c.status === "active");
        setSelectedCycle(cur ? (cur.cycleId || cur.id) : "");
      })
      .catch(() => { if (active) setSelectedCycle(""); });
    return () => { active = false; };
  }, [getToken]);

  // ── Fetch all 6 domains in parallel ──────────────────────────────────────────
  useEffect(() => {
    if (selectedCycle === null) return; // wait for cycle init
    let active = true;
    setLoading(true);
    setError(null);

    const DOMAIN_NAMES = ["throughput", "fulfillment", "pickup", "catalog", "volunteer", "cycle"];

    getToken()
      .then(token => {
        const h = { Authorization: `Bearer ${token}` };
        const cq = selectedCycle ? `&cycleId=${encodeURIComponent(selectedCycle)}` : "";
        const base = `/api/metrics?layer=A`;
        return Promise.allSettled([
          fetch(`${base}&domain=throughput${cq}`, { headers: h }).then(r => r.json()),
          fetch(`${base}&domain=fulfillment${cq}`, { headers: h }).then(r => r.json()),
          fetch(`${base}&domain=pickup${cq}`, { headers: h }).then(r => r.json()),
          fetch(`${base}&domain=catalog`, { headers: h }).then(r => r.json()),
          fetch(`${base}&domain=volunteer${cq}`, { headers: h }).then(r => r.json()),
          fetch(`${base}&domain=cycle${cq}`, { headers: h }).then(r => r.json()),
        ]);
      })
      .then(results => {
        if (!active) return;
        const [throughput, fulfillment, pickup, catalog, volunteer, cycle] =
          results.map(r => (r.status === "fulfilled" ? r.value : null));
        setData({ throughput, fulfillment, pickup, catalog, volunteer, cycle });
        const failed = DOMAIN_NAMES.filter((_, i) => results[i].status === "rejected");
        setError(failed.length ? `Some metrics sections could not load: ${failed.join(", ")}` : null);
      })
      .catch(e => { if (active) setError(e.message || "Failed to load metrics"); })
      .finally(() => { if (active) setLoading(false); });

    return () => { active = false; };
  }, [getToken, selectedCycle, refreshKey]);

  // ── Derived ───────────────────────────────────────────────────────────────────
  const isInitialLoad = loading && !data;
  const isError       = error && !data;

  return (
    <div style={{ padding: "24px 0", background: "#F7FAFD", minHeight: "100%" }}>

      {/* ── Header ─────────────────────────────────────────────────────────────── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700, color: NAVY, margin: 0 }}>Program Metrics</h2>
          <p style={{ fontSize: 13, color: "#718096", margin: "4px 0 0" }}>
            Operational dashboard — Raider Depot pilot
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <select
            value={selectedCycle ?? ""}
            onChange={e => setSelectedCycle(e.target.value)}
            style={{ border: `1px solid ${LIGHT}`, borderRadius: 8, padding: "8px 12px", fontSize: 13, background: "#FFFFFF", color: NAVY, cursor: "pointer", outline: "none" }}
          >
            <option value="">All cycles</option>
            {cycles.map(c => {
              const cid = c.cycleId || c.id;
              return (
                <option key={cid} value={cid}>
                  {formatCycleDate(cid)} — {c.status}
                </option>
              );
            })}
          </select>
          <button
            onClick={() => setRefreshKey(k => k + 1)}
            disabled={loading}
            style={{ background: NAVY, color: "#FFFFFF", border: "none", borderRadius: 8, padding: "8px 18px", fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", opacity: loading ? 0.7 : 1 }}
          >
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {/* ── Loading ────────────────────────────────────────────────────────────── */}
      {isInitialLoad && (
        <div style={{ padding: "48px 0", textAlign: "center", color: "#718096", fontSize: 15 }}>
          Metrics — Loading operational dashboard...
        </div>
      )}

      {/* ── Full error (no data at all) ─────────────────────────────────────────── */}
      {isError && (
        <div style={{ background: "#FFFFFF", border: `1.5px solid ${LIGHT}`, borderRadius: 12, padding: "48px 32px", textAlign: "center" }}>
          <div style={{ fontSize: 20, fontWeight: 700, color: NAVY, marginBottom: 8 }}>Program Metrics</div>
          <div style={{ fontSize: 14, color: "#718096" }}>
            Operational dashboard is being set up. Check back shortly.
          </div>
        </div>
      )}

      {/* ── Partial error (some domains failed, rest of dashboard still renders) ── */}
      {error && data && (
        <div style={{ background: "#FEF3C7", border: "1px solid #FCD34D", borderRadius: 8, padding: "10px 16px", marginBottom: 16, fontSize: 13, color: "#92400E" }}>
          {error}. Other sections are shown below.
        </div>
      )}

      {/* ── Dashboard ──────────────────────────────────────────────────────────── */}
      {data && (() => {
        const { throughput, fulfillment, pickup, catalog, volunteer, cycle } = data;
        const totalOrders = throughput?.orders_submitted ?? 0;
        const statusMap   = throughput?.orders_by_status ?? {};
        const noShowCount = (pickup?.no_show_count ?? 0);

        return (
          <>
            {/* KPI row */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 18 }}>
              <StatCard
                label="Orders Submitted"
                value={totalOrders.toLocaleString()}
                sub={selectedCycle ? formatCycleDate(selectedCycle) : "all cycles"}
                accent={NAVY}
              />
              <StatCard
                label="Pickup Rate"
                value={`${pickup?.pickup_rate ?? 0}%`}
                sub={`${(pickup?.picked_up_count ?? 0).toLocaleString()} picked up`}
                accent="#16A34A"
              />
              <StatCard
                label="No-Show Rate"
                value={`${pickup?.no_show_rate ?? 0}%`}
                sub={`${noShowCount.toLocaleString()} no-shows`}
                accent="#DC2626"
              />
              <StatCard
                label="Volunteer Headcount"
                value={(volunteer?.volunteer_headcount ?? 0).toLocaleString()}
                sub={`avg ${volunteer?.avg_orders_per_volunteer ?? 0} orders/vol`}
                accent={MID}
              />
              <StatCard
                label="Active Catalog Items"
                value={(catalog?.active_catalog_size ?? 0).toLocaleString()}
                sub={`${catalog?.items_below_reorder?.length ?? 0} below reorder · ${catalog?.items_not_available_this_cycle?.length ?? 0} not this cycle`}
                accent={BRIGHT}
              />
            </div>

            {/* Status breakdown + Velocity */}
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              <SectionCard title="Orders by Status" style={{ flex: "1 1 260px" }}>
                {totalOrders === 0 ? (
                  <div style={{ fontSize: 13, color: "#9CA3AF", textAlign: "center", padding: "12px 0" }}>
                    No orders in this period
                  </div>
                ) : (
                  Object.entries(STATUS_CONFIG).map(([key, cfg]) => {
                    const count = statusMap[key] || 0;
                    if (!count) return null;
                    return (
                      <RateBar
                        key={key}
                        label={cfg.label}
                        count={count}
                        total={totalOrders}
                        color={cfg.color}
                      />
                    );
                  })
                )}
              </SectionCard>

              <SectionCard title="Submission Velocity" style={{ flex: "2 1 300px" }}>
                <div style={{ fontSize: 12, color: "#718096", marginBottom: 10 }}>
                  Orders per day — {selectedCycle ? formatCycleDate(selectedCycle) : "all cycles"}
                </div>
                <VelocityChart data={throughput?.submission_velocity ?? []} />
              </SectionCard>
            </div>

            {/* Fulfillment */}
            <SectionCard title="Fulfillment">
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>

                <div style={{ flex: "1 1 220px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
                    Volunteer Claim Rate
                  </div>
                  <RateBar
                    label="Orders claimed"
                    rate={fulfillment?.volunteer_claim_rate ?? 0}
                    color={MID}
                  />
                </div>

                <div style={{ flex: "0 0 150px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
                    Avg Pack Time
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 700, color: NAVY, lineHeight: 1 }}>
                    {fulfillment?.avg_packing_time_min != null
                      ? `${fulfillment.avg_packing_time_min} min`
                      : "—"}
                  </div>
                  <div style={{ fontSize: 12, color: "#718096", marginTop: 4 }}>
                    claim to packed
                  </div>
                </div>

                <div style={{ flex: "1 1 220px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 10 }}>
                    Flagged Orders — {fulfillment?.flagged_order_count ?? 0}
                    {(fulfillment?.flagged_order_count ?? 0) > 0 && (
                      <span style={{ fontWeight: 400, marginLeft: 4 }}>({fulfillment.flagged_order_rate}%)</span>
                    )}
                  </div>
                  {(fulfillment?.flagged_order_count ?? 0) > 0 ? (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      {Object.entries(fulfillment?.flagged_by_reason ?? {}).map(([reason, count]) => (
                        <span key={reason} style={{ background: "#FEF3C7", color: "#92400E", fontSize: 12, fontWeight: 500, padding: "4px 10px", borderRadius: 20 }}>
                          {FLAG_LABELS[reason] || reason}: {count}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div style={{ fontSize: 13, color: "#16A34A" }}>No flagged orders</div>
                  )}
                </div>
              </div>
            </SectionCard>

            {/* Catalog */}
            <SectionCard title="Catalog">
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>

                <div style={{ flex: "0 0 130px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    Active Items
                  </div>
                  <div style={{ fontSize: 32, fontWeight: 700, color: NAVY, lineHeight: 1 }}>
                    {(catalog?.active_catalog_size ?? 0).toLocaleString()}
                  </div>
                  <div style={{ fontSize: 12, color: "#718096", marginTop: 4 }}>in stock, available</div>
                </div>

                <div style={{ flex: "1 1 240px" }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                    Below Reorder Threshold ({catalog?.items_below_reorder?.length ?? 0})
                  </div>
                  {(catalog?.items_below_reorder?.length ?? 0) > 0 ? (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr>
                          {["Item", "On Hand", "Threshold"].map(h => (
                            <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {catalog.items_below_reorder.map(item => (
                          <tr key={item.itemId}>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9" }}>{item.name}</td>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9", fontWeight: 700, color: "#DC2626" }}>
                              {item.onHand}
                            </td>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9", color: "#718096" }}>
                              {item.reorderThreshold}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 13, color: "#16A34A" }}>All items are adequately stocked</div>
                  )}
                </div>

                {(catalog?.items_not_available_this_cycle?.length ?? 0) > 0 && (
                  <div style={{ flex: "1 1 240px" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                      In Stock, Not This Cycle ({catalog.items_not_available_this_cycle.length}) — {selectedCycle}
                    </div>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                      <thead>
                        <tr>
                          {["Item", "On Hand"].map(h => (
                            <th key={h} style={{ textAlign: "left", padding: "4px 8px", color: "#64748b", fontSize: 11, fontWeight: 600, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {catalog.items_not_available_this_cycle.map(item => (
                          <tr key={item.itemId}>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9" }}>{item.name}</td>
                            <td style={{ padding: "6px 8px", borderBottom: "1px solid #f1f5f9", fontWeight: 600, color: "#4A5568" }}>
                              {item.onHand}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {(catalog?.top_demanded_items?.length ?? 0) > 0 && (
                  <div style={{ flex: "1 1 240px" }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#718096", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
                      Top Demanded Items
                    </div>
                    {catalog.top_demanded_items.map((item, i) => (
                      <div key={item.itemId} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: "1px solid #f1f5f9", fontSize: 13 }}>
                        <span style={{ color: "#4A5568" }}>
                          <span style={{ color: "#9CA3AF", marginRight: 6 }}>{i + 1}.</span>
                          {item.name}
                        </span>
                        <span style={{ fontWeight: 600, color: NAVY }}>{item.totalQty.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </SectionCard>

            {/* Cycle-over-cycle table */}
            {(cycle?.cycle_over_cycle?.length ?? 0) > 0 && (
              <div style={{ background: "#FFFFFF", border: `1.5px solid ${LIGHT}`, borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
                <div style={{ padding: "12px 20px", borderBottom: `1px solid ${LIGHT}` }}>
                  <h3 style={{ fontSize: 14, fontWeight: 700, color: NAVY, margin: 0 }}>
                    Cycle-over-Cycle (last {cycle.cycle_over_cycle.length})
                  </h3>
                </div>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: NAVY }}>
                        {["Cycle", "Status", "Submitted", "Packed", "Picked Up", "No-Show", "Flagged"].map(h => (
                          <th key={h} style={{ textAlign: "left", padding: "10px 14px", color: "#FFFFFF", fontWeight: 600, fontSize: 12, whiteSpace: "nowrap" }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {cycle.cycle_over_cycle.map((c, i) => {
                        const noShow = (c.no_show || 0) + (c.not_picked_up || 0);
                        return (
                          <tr key={c.cycleId} style={{ background: i % 2 === 0 ? "#FAFBFF" : "#FFFFFF" }}>
                            <td style={{ padding: "9px 14px", borderBottom: "1px solid #EDF2F7", fontWeight: 600, color: NAVY, whiteSpace: "nowrap" }}>
                              {formatCycleDate(c.cycleId)}
                            </td>
                            <td style={{ padding: "9px 14px", borderBottom: "1px solid #EDF2F7" }}>
                              <CycleStatusBadge status={c.status} />
                            </td>
                            <td style={{ padding: "9px 14px", borderBottom: "1px solid #EDF2F7" }}>
                              {(c.submitted || 0).toLocaleString()}
                            </td>
                            <td style={{ padding: "9px 14px", borderBottom: "1px solid #EDF2F7" }}>
                              {(c.packed || 0).toLocaleString()}
                            </td>
                            <td style={{ padding: "9px 14px", borderBottom: "1px solid #EDF2F7", fontWeight: 600, color: "#15803D" }}>
                              {(c.picked_up || 0).toLocaleString()}
                            </td>
                            <td style={{ padding: "9px 14px", borderBottom: "1px solid #EDF2F7", color: noShow > 0 ? "#DC2626" : "#6B7280" }}>
                              {noShow.toLocaleString()}
                            </td>
                            <td style={{ padding: "9px 14px", borderBottom: "1px solid #EDF2F7", color: (c.flagged || 0) > 0 ? "#D97706" : "#6B7280" }}>
                              {(c.flagged || 0).toLocaleString()}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
}
